import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import type { UserPublic } from "@/client"
import { OrganizationsService, UsersService } from "@/client"
import type { MembershipPublic } from "@/client/types.gen"
import StudentDashboard from "@/components/Dashboard/StudentDashboard"
import TeacherDashboard from "@/components/Dashboard/TeacherDashboard"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { isLoggedIn } from "@/hooks/useAuth"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/")({
  component: DashboardPage,
})

function DashboardPage() {
  const { dashboardLabel } = useStudentLabels()
  const breadcrumbs = useRouteBreadcrumbs("dashboard.root")

  // Use useQuery to properly handle loading state for currentUser
  const { data: currentUser, isLoading: isLoadingUser } = useQuery<
    UserPublic | null,
    Error
  >({
    queryKey: ["currentUser"],
    queryFn: UsersService.readUserMe,
    enabled: isLoggedIn(),
  })

  // Get user memberships to determine role
  const { data: membershipsData, isLoading: isLoadingMemberships } = useQuery<
    MembershipPublic[]
  >({
    queryKey: ["myMemberships", currentUser?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: !!currentUser && !currentUser.is_superuser,
  })

  // Wait for currentUser to be loaded first
  if (isLoadingUser || !currentUser) {
    return (
      <PageShell title={dashboardLabel} breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="stats" />
      </PageShell>
    )
  }

  // Don't render dashboard until we know the user's role
  // For superusers, we know immediately. For others, wait for memberships data
  const isRoleKnown =
    currentUser.is_superuser ||
    (!isLoadingMemberships && membershipsData !== undefined)

  if (!isRoleKnown) {
    return (
      <PageShell title={dashboardLabel} breadcrumbs={breadcrumbs}>
        <PageSkeleton variant="stats" />
      </PageShell>
    )
  }

  const memberships = membershipsData ?? []

  // Determine if user is teacher/admin/superuser for any org
  const isTeacherOrAdmin =
    currentUser.is_superuser ||
    memberships.some((m) => m.role === "teacher" || m.role === "admin")

  return (
    <PageShell
      title={dashboardLabel}
      breadcrumbs={breadcrumbs}
      gap={0}
      description={
        isTeacherOrAdmin
          ? "Monitor quiz performance across your organization."
          : "Review upcoming quizzes and recent results."
      }
    >
      {isTeacherOrAdmin ? <TeacherDashboard /> : <StudentDashboard />}
    </PageShell>
  )
}
